// Persistence (§27.3). Every read and write is wrapped in try/catch: storage can be missing,
// blocked (private mode, sandboxed iframes) or full, and the game must run on defaults then.

export const STORAGE_KEYS = {
  settings: 'shoveoff.settings.v1',
  profile: 'shoveoff.profile.v1',
  bindings: 'shoveoff.bindings.v1',
  /** sessionStorage: `{ code, token }` for reconnecting after a reload or socket loss (§9.6). */
  reconnect: 'shoveoff.reconnect',
} as const;

type Area = 'local' | 'session';

function area(kind: Area): Storage | null {
  try {
    return kind === 'local' ? globalThis.localStorage : globalThis.sessionStorage;
  } catch {
    return null;
  }
}

/** Parsed JSON stored under `key`, or undefined when missing, unreadable or malformed. */
export function readJson(key: string, kind: Area = 'local'): unknown {
  try {
    const raw = area(kind)?.getItem(key);
    return raw == null ? undefined : (JSON.parse(raw) as unknown);
  } catch {
    return undefined;
  }
}

/** Stores `value` as JSON; returns false when storage is unavailable or full. */
export function writeJson(key: string, value: unknown, kind: Area = 'local'): boolean {
  try {
    const s = area(kind);
    if (!s) return false;
    s.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function removeKey(key: string, kind: Area = 'local'): void {
  try {
    area(kind)?.removeItem(key);
  } catch {
    // Storage unavailable: nothing to remove.
  }
}

export interface ReconnectInfo { code: string; token: string }

export function loadReconnect(): ReconnectInfo | null {
  const v = readJson(STORAGE_KEYS.reconnect, 'session');
  if (typeof v !== 'object' || v === null) return null;
  const { code, token } = v as Record<string, unknown>;
  return typeof code === 'string' && typeof token === 'string' && code !== '' && token !== '' ? { code, token } : null;
}

export function saveReconnect(info: ReconnectInfo): void {
  writeJson(STORAGE_KEYS.reconnect, info, 'session');
}

export function clearReconnect(): void {
  removeKey(STORAGE_KEYS.reconnect, 'session');
}
